/** The chunks worth fetching ahead, in table order, for warmRouteChunks. */
const chunkLoaders: (() => Promise<unknown>)[] = []

/**
 * Marks a route chunk to fetch ahead of the first tap. Only for screens
 * most opens reach: the bottom-nav tabs and the screen each leads to.
 * Everything else (editor, payments, coach tools) loads when opened.
 */
export function warmChunk<M>(load: () => Promise<M>): () => Promise<M> {
    chunkLoaders.push(load)
    return load
}

type NetworkInformation = { saveData?: boolean; effectiveType?: string }

/** Data saver on, or a 2G-class link: no speculative downloads. */
function constrainedNetwork(): boolean {
    const connection = (
        navigator as Navigator & { connection?: NetworkInformation }
    ).connection
    return (
        !!connection?.saveData ||
        connection?.effectiveType === 'slow-2g' ||
        connection?.effectiveType === '2g'
    )
}

/**
 * Fetches the marked chunks one by one while the app is idle, so the first
 * tap on a tab does not wait for its code. A failed chunk is left for
 * React Router to fetch (and report) when the route is opened.
 */
export function warmRouteChunks(): void {
    if (constrainedNetwork()) return
    const idle = (next: () => void) =>
        typeof window.requestIdleCallback === 'function'
            ? window.requestIdleCallback(next, { timeout: 2000 })
            : window.setTimeout(next, 200)
    const queue = [...chunkLoaders]
    const step = () => {
        const load = queue.shift()
        if (!load) return
        load().then(
            () => idle(step),
            () => idle(step)
        )
    }
    idle(step)
}
